import * as React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import apiClient from '../services/api-client';
import { sanitizeSvg, tintSvgCurrentColor } from '../services/sanitizeSvg';

// Module-level cache: typeKey -> sanitized SVG string (or '' if failed).
// Survives across mounts so icons aren't refetched on every navigation.
const svgCache = new Map();

// In-flight requests: typeKey -> Promise<string>. Avoids duplicate fetches
// when multiple GestionTypeIcon instances for the same type mount at once.
const inflight = new Map();

async function fetchGestionIcon(typeKey) {
  if (svgCache.has(typeKey)) {
    return svgCache.get(typeKey);
  }
  if (inflight.has(typeKey)) {
    return inflight.get(typeKey);
  }

  const promise = (async () => {
    try {
      const response = await apiClient.get(
        `gestiones/type/${encodeURIComponent(typeKey)}/icon`,
        { parseAppConfig: false, axiosConfig: { responseType: 'text' } },
        { action: 'fetch_gestion_type_icon' }
      );
      const raw = typeof response.data === 'string' ? response.data : '';
      const sanitized = tintSvgCurrentColor(sanitizeSvg(raw));
      svgCache.set(typeKey, sanitized);
      return sanitized;
    } catch (err) {
      svgCache.set(typeKey, '');
      return '';
    } finally {
      inflight.delete(typeKey);
    }
  })();

  inflight.set(typeKey, promise);
  return promise;
}

export function clearGestionIconCache() {
  svgCache.clear();
  inflight.clear();
}

/**
 * Renders a gestion-type SVG icon (fetched from
 * `/api/gestiones/type/{typeKey}/icon`), sanitized and injected via
 * dangerouslySetInnerHTML. Falls back to a generic MUI icon while loading
 * or if the fetch/parse fails. Respects dark/light mode via currentColor.
 *
 * Props:
 *  - typeKey: gestion type key (required)
 *  - size:    pixel size (default 24)
 *  - color:   optional CSS color; defaults to currentColor so the icon
 *             inherits the surrounding text color / theme.
 */
const GestionTypeIcon = ({ typeKey, size = 24, color, sx, ...rest }) => {
  const theme = useTheme();
  const [svg, setSvg] = React.useState(() => (typeKey ? svgCache.get(typeKey) ?? null : null));

  React.useEffect(() => {
    if (!typeKey) {
      return;
    }
    const cached = svgCache.get(typeKey);
    if (cached !== undefined) {
      setSvg(cached);
      return;
    }
    let active = true;
    setSvg(null);
    fetchGestionIcon(typeKey).then((result) => {
      if (active) {
        setSvg(result);
      }
    });
    return () => {
      active = false;
    };
  }, [typeKey]);

  const resolvedColor = color || 'currentColor';
  const boxSize = size;

  if (!svg) {
    return (
      <ExtensionRoundedIcon
        sx={{ color: resolvedColor, fontSize: `${boxSize}px`, ...sx }}
        {...rest}
      />
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: boxSize,
        height: boxSize,
        color: resolvedColor,
        lineHeight: 0,
        '& svg': {
          width: '100%',
          height: '100%',
          display: 'block',
        },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
      {...rest}
    />
  );
};

export default GestionTypeIcon;
